import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * One main-client row managed by `MainClientsForm`'s `useFieldArray`. Mirrors
 * `CompanyMainClientRequest` (`types/company.type.ts`) field-for-field — deliberately NOT
 * `CompanyMainClientResponse`'s `id`/`description`. Neither the backend nor the pre-Carbon UI has
 * ever carried either for a SAVE: `CompanyProfileUpdateRequest.ClientRequest`'s own source
 * comment reads "Removed description, added person contact info as requested by FE team" (and
 * `CompanyService#updateCompanyClients` never reads or persists a description), and the
 * pre-Carbon `MainClientsForm.tsx` (verified by reading it before rewriting) never rendered a
 * description control either — despite this task's own brief describing the row as "client
 * name/description/contact person/email/phone". Same disclosed brief-vs-reality discrepancy Task
 * 8 found for BasicInformationForm's "logo" upload and Task 9 found for banking's
 * `bankIdentifier`: not reinvented here, `id` and `description` stay absent from both the Yup
 * shape and the payload.
 */
export interface MainClientFormFields {
  clientName: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
}

export interface MainClientsFormFields {
  mainClients: MainClientFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * All four fields are `.required()` — reproducing the pre-Carbon "Add" button's own `disabled`
 * condition EXACTLY (`!newMainClient.clientName || !newMainClient.contactPersonName ||
 * !newMainClient.contactEmail || !newMainClient.contactPhone`). That gate is also why requiring
 * all four for every row — not just new ones — is safe: the only path that ever created a
 * `CompanyClient` row was through this exact gate, so every already-persisted client already
 * satisfies it.
 *
 * `contactEmail` additionally carries `.email()` — the backend's own `ClientRequest.contactEmail`
 * is `@Email` (this task's brief calls this out explicitly), same rationale as
 * `contactsInformationValidationSchema.ts`'s own `email` field: the pre-Carbon `type="email"`
 * input never enforced format client-side (browser constraint validation never ran — this form,
 * like every pre-Carbon sub-form, was never inside a submitted `<form>`), so this is a new,
 * strictly-safer gate, not a new restriction on what was ever actually saved.
 *
 * Max-lengths mirror `ClientRequest`'s own `@Size` constraints exactly: `clientName`/
 * `contactPersonName`/`contactEmail` 255, `contactPhone` 50.
 */
export const mainClientRowValidationSchema = Yup.object({
  clientName: Yup.string()
    .required(i18next.t("validations.clientName"))
    .max(255, maxLengthMessage(255)),
  contactPersonName: Yup.string()
    .required(i18next.t("validations.contactPersonName"))
    .max(255, maxLengthMessage(255)),
  contactEmail: Yup.string()
    .required(i18next.t("validations.email"))
    .email(i18next.t("validations.validEmail"))
    .max(255, maxLengthMessage(255)),
  contactPhone: Yup.string()
    .required(i18next.t("validations.phone"))
    .max(50, maxLengthMessage(50)),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero main clients was, and stays, a valid
 * state) — only each staged row, at the moment it was added. No `.min()`/`.required()` on the
 * array, same rationale as every other repeatable sub-form's schema (Tasks 8-9).
 */
export const mainClientsValidationSchema = Yup.object({
  mainClients: Yup.array().of(mainClientRowValidationSchema).default([]),
});
